(() => {
    class ManageCampaignTemplateBasicController {
        _campaignTemplate;
        /**
         * @type {FilteredCheckListOptions}
         */
        targetSourcesOptions = {
            label: "Select target source(s)",
            source: [],
            target: [],
            itemText: o => o.DisplayName,
            itemTitle: o => `${o.Source}&#013;&#013;${o.Description}`
        }

        /**
         * @type {CustomerWorkflowType[]}
         */
        customerWorkflowTypes = [];

        get campaignTemplate() {
            return this._campaignTemplate;
        }

        set campaignTemplate(value) {
            if (!Array.isArray(value.CampaignTemplateTargetSources)) {
                value.CampaignTemplateTargetSources = [];
            }
            this._campaignTemplate = value;
        }

        _loadingDepth = 0;

        get isLoading() {
            return !!this._loadingDepth;
        }

        set isLoading(value) {
            if (value) {
                this._loadingDepth++;
            } else {
                this._loadingDepth--;
                if (this._loadingDepth < 0) {
                    this._loadingDepth = 0;
                }
            }
        }

        get workflowTypeTooltip() {
            const selected = (this.customerWorkflowTypes || [])
                .find(o => o.Id === this.campaignTemplate.CustomerWorkflowTypeId);
            if (!selected) {
                return "(Please select a workflow type)";
            }
            return selected.Description || "(no description set for this workflow type)"
        }

        constructor(
            $scope,
            $state,
            CampaignDesignerService,
            PermissionService,
            notify,
            ErrorService,
            NgTableParams,
            LocalisationService,
            $uibModal
        ) {
            this.$scope = $scope;
            this.$state = $state;
            this.CampaignDesignerService = CampaignDesignerService;
            this.notify = notify;
            this.ErrorService = ErrorService;
            this.NgTableParams = NgTableParams;
            this.LocalisationService = LocalisationService;
            this.userContext = PermissionService.userContext;
            this.modal = $uibModal;

            this.isLoading = false;
            this.customerWorkflowTypes = [];
        }

        async $onInit() {
            if (!this.campaignTemplate) {
                throw new Error(`campaignTemplate prop not passed in`);
            }

            try {
                this.isLoading = true;

                this.campaignTemplate.PublishFrom = new Date(this.campaignTemplate.PublishFrom);
                this.campaignTemplate.PublishTo = new Date(this.campaignTemplate.PublishTo);
                this.campaignTemplate.StartDate = new Date(this.campaignTemplate.StartDate);
                this.campaignTemplate.EndDate = new Date(this.campaignTemplate.EndDate);

                const now = new Date();

                this.publishFromDatePickerOptions = {
                    formatYear: 'yy',
                    startingDay: 1,
                    showWeeks: false,
                    dateDisabled: false,
                    showDatePicker: false,
                    minDate: now
                };

                this.publishToDatePickerOptions = {
                    formatYear: 'yy',
                    startingDay: 1,
                    showWeeks: false,
                    dateDisabled: false,
                    showDatePicker: false
                };
                this.startDatePickerOptions = {
                    formatYear: 'yy',
                    startingDay: 1,
                    showWeeks: false,
                    dateDisabled: false,
                    showDatePicker: false
                };
                this.endDatePickerOptions = {
                    formatYear: 'yy',
                    startingDay: 1,
                    showWeeks: false,
                    dateDisabled: false,
                    showDatePicker: false
                };

                this.ObjectiveSettings = {
                    smartButtonMaxItems: 3,
                    displayProp: "Name"
                };

                this.editorConfig = {
                    language: 'en',
                    allowedContent: true,
                    fullPage: true,
                    extraPlugins: 'sourcedialog',
                    removePlugins: 'sourcearea',
                    versionCheck: false
                };

                this.$scope.$watch('$ctrl.campaignTemplate.PublishFrom', () => this.PublishFromDateChanged());
                this.$scope.$watch('$ctrl.campaignTemplate.PublishTo', () => this.PublishToDateChanged());
                this.$scope.$watch('$ctrl.campaignTemplate.StartDate', () => this.StartDateChanged());
                this.$scope.$watch('$ctrl.campaignTemplate.EndDate', () => this.EndDateChanged());

                this.statuses = Object.entries(Dashboard.Models.Lookups.CampaignTargetSourceStatuses).map(([ key, value ]) => ({
                    key,
                    value
                }));
                this.types = Object.entries(Dashboard.Models.Lookups.CampaignLevels).map(([ key, value ]) => ({
                    key,
                    value
                }));

                this.loadAllowedStartDates();
                this.loadAllowedEndDates();

                await Promise.all([
                    this.loadWorkflowTypes(),
                    this.loadCampaignObjectives(),
                    this.loadTargetSources()
                ]);
            } catch (e) {
                throw e;
            } finally {
                this.isLoading = false;
            }
        }

        loadCampaignObjectives() {
            this.ObjectiveOptions = [];
            this.isLoading = true;

            return this.CampaignDesignerService.getCampaignObjectives()
                .then(result => {
                    this.ObjectiveOptions = result.data;
                    this.filterObjectives();
                })
                .finally(() => {
                        this.isLoading = false;
                    }
                );
        }

        async loadTargetSources() {
            try {
                this.isLoading = true;
                const filter = {
                    BrandId: this.userContext.BrandId,
                    Name: "",
                    Type: this.campaignTemplate.CampaignLevelId,
                    Status: Dashboard.Models.Lookups.CampaignTargetSourceStatuses.All
                }
                const result = await this.CampaignDesignerService.getTargetSourcesForFilter(
                    null,
                    null,
                    filter);
                const { data } = result;
                this.targetSourcesOptions.source.splice(0, this.targetSourcesOptions.source.length);
                this.targetSourcesOptions.source.push(
                    ...data.CampaignTargetSources.map(x => {
                        x.DisplayName = `${x.StatusId === Dashboard.Models.Lookups.CampaignTargetSourceStatuses.Test ? "(TEST) - " : ""}${x.Name}`
                        x.TypeName = this.types.find(y => y.value === x.CampaignLevelId).key;
                        x.StatusName = this.statuses.find(s => s.value === x.StatusId).key;
                        return x;
                    }).filter(
                        y => y.StatusId !== Dashboard.Models.Lookups.CampaignTargetSourceStatuses.Archived
                    )
                );
                this.targetSourcesOptions.target.splice(0, this.targetSourcesOptions.target.length);
                for (const source of this.campaignTemplate.CampaignTemplateTargetSources) {
                    const match = this.targetSourcesOptions.source.find(
                        x => x.Id === source.CampaignTargetSourceId
                    )
                    if (match) {
                        this.targetSourcesOptions.target.push(match)
                    } else {
                        console.error(`can't find source CampaignTargetSource to match id: ${source.CampaignTargetSourceId}`,
                            this.targetSourcesOptions.source
                        );
                    }
                }
            } catch (e) {
                this.ErrorService.showError("An error occurred when trying to load target sources", e);
            } finally {
                this.isLoading = false;
                this.$scope.$apply();
            }
        }

        filterObjectives() {
            const lookup = _.indexBy(
                this.campaignTemplate.CampaignObjectives,
                item => item.Id
            );

            this.campaignTemplate.CampaignObjectives = (this.ObjectiveOptions || [])
                .filter(objective =>
                    lookup[objective.Id] !== undefined
                );
        }

        loadWorkflowTypes() {
            this.isLoading = true;

            return this.CampaignDesignerService.getCustomerWorkflowTypes()
                .then(result => {
                    this.customerWorkflowTypes = result.data;
                    if (!this.campaignTemplate.CustomerWorkflowTypeId) {
                        const firstDefault = this.customerWorkflowTypes.find(o => o.IsDefault);
                        if (!!firstDefault) {
                            this.campaignTemplate.CustomerWorkflowTypeId = firstDefault.Id
                        } else if (!!this.customerWorkflowTypes[0]) {
                            this.campaignTemplate.CustomerWorkflowTypeId = this.customerWorkflowTypes[0].Id;
                        }
                    }
                })
                .finally(() => this.isLoading = false);
        }

        loadAllowedStartDates() {
            this.allowedStartDates = this.CampaignDesignerService.getCampaignTemplateDateRules();
        }

        loadAllowedEndDates() {
            this.allowedEndDates = this.CampaignDesignerService.getCampaignTemplateDateRules();
        }

        canChangeWorkflowTypes() {
            return (this.customerWorkflowTypes || []).length > 0 &&
                this.campaignTemplate.IsDraft();
        }

        formatDate(date) {
            return this.LocalisationService.formatDateShort(date);
        }

        fixDisplayOrders() {
            for (let i = 0; i < this.campaignTemplate.CampaignTemplateTargetSources.length; i++) {
                this.campaignTemplate.CampaignTemplateTargetSources[i].DisplayOrder = i + 1;
            }
        }

        setCampaignTemplateTargetSourcesFromSelection() {
            const rawSources = [ ...this.targetSourcesOptions.target ];
            this.campaignTemplate.CampaignTemplateTargetSources.splice(
                0,
                this.campaignTemplate.CampaignTemplateTargetSources.length
            );
            for (const source of rawSources) {
                this.campaignTemplate.CampaignTemplateTargetSources.push({
                    CampaignTargetSourceId: source.Id,
                    Name: source.Name,
                    Description: source.Description,
                    Source: source.Source,
                    StatusId: source.StatusId,
                    LastModified: source.LastModified
                });
            }
            this.fixDisplayOrders();
        }

        submitForm() {
            if (this.isLoading) {
                return;
            }
            this.isLoading = true;
            if (this.campaignTemplate.CampaignLevelId === window.Dashboard.Models.Lookups.CampaignLevels.Brand) {
                this.campaignTemplate.StoreAssociations = [];
            }
            this.setCampaignTemplateTargetSourcesFromSelection();

            this.campaignTemplate.BrandId = this.userContext.BrandId;
            const validationPassed = this.campaignTemplate.Validate(this.ErrorService);
            if (!validationPassed) {
                this.isLoading = false;
                return;
            }
            return this.saveTemplate();
        }

        saveTemplate() {
            // Save the basic setup for the campaign template
            return this.CampaignDesignerService.saveBasicSetup(this.campaignTemplate)
                .then(result => {
                        this.notify.success(
                            "The campaign template basic details were successfully saved"
                        );
                        this.campaignTemplate.TemplateGuid = result.data;
                        // If this is a brand level campaign, skip store associations
                        const goto = this.campaignTemplate.CampaignLevelId === Dashboard.Models.Lookups.CampaignLevels.Brand
                            ? ".vouchers"
                            : ".stores";
                        this.$state.go(goto, {
                            brandId: this.$state.params.brandId,
                            CampaignTemplateGuid: result.data
                        });
                    }
                ).catch(err => {
                        this.ErrorService.showError(
                            "An error occurred while trying to save the campaign",
                            err
                        );
                    }
                ).finally(() => {
                        this.isLoading = false;
                    }
                );
        }

        previewTermsUrl() {
            const url = this.campaignTemplate.GetFormattedTermsUrl();
            if (!url) {
                return;
            }
            window.open(url, '_blank');
        }

        openPublishFromDatePicker() {
            this.publishFromdDatePickerOptions.showDatePicker = true;
        }

        openPublishToDatePicker() {
            this.publishToDatePickerOptions.showDatePicker = true;
        }

        openStartDatePicker() {
            this.startDatePickerOptions.showDatePicker = true;
        }

        openEndDatePicker() {
            this.endDatePickerOptions.showDatePicker = true;
        };

        PublishFromDateChanged() {
            if (this.campaignTemplate.PublishFrom) {
                this.publishToDatePickerOptions.minDate = new Date(this.campaignTemplate.PublishFrom);
                this.startDatePickerOptions.minDate = new Date(this.campaignTemplate.PublishFrom);

                if (this.campaignTemplate.PublishFrom > this.campaignTemplate.PublishTo) {
                    this.campaignTemplate.PublishTo = this.campaignTemplate.PublishFrom;
                }

                if (this.campaignTemplate.PublishFrom > this.campaignTemplate.StartDate) {
                    this.campaignTemplate.StartDate = this.campaignTemplate.PublishFrom;
                }
            }
        }

        PublishToDateChanged() {
            if (this.campaignTemplate.PublishTo) {
                this.startDatePickerOptions.maxDate = new Date(this.campaignTemplate.PublishTo);
                this.endDatePickerOptions.maxDate = new Date(this.campaignTemplate.PublishTo);

                if (this.campaignTemplate.PublishFrom > this.campaignTemplate.PublishTo) {
                    this.campaignTemplate.PublishTo = this.campaignTemplate.PublishFrom;
                }

                if (this.campaignTemplate.PublishTo < this.campaignTemplate.endDate) {
                    this.campaignTemplate.endDate = this.campaignTemplate.PublishTo;
                }

                if (this.campaignTemplate.PublishTo < this.campaignTemplate.StartDate) {
                    this.campaignTemplate.StartDate = this.campaignTemplate.PublishTo;
                }
            }
        }

        StartDateChanged() {
            if (this.campaignTemplate.StartDate) {
                this.endDatePickerOptions.minDate = new Date(this.campaignTemplate.StartDate);

                if (this.campaignTemplate.StartDate > this.campaignTemplate.EndDate) {
                    this.campaignTemplate.EndDate = this.campaignTemplate.StartDate;
                }
            }
        }

        EndDateChanged() {
            if (this.campaignTemplate.EndDate) {
                if (this.campaignTemplate.StartDate > this.campaignTemplate.EndDate) {
                    this.campaignTemplate.EndDate = this.campaignTemplate.StartDate
                }
            }
        }


        async campaignLevelIdChanged() {
            if (this.campaignTemplate.CampaignTemplateTargetSources.length > 0) {
                const modalTemplateUrl = "components/campaigndesigner/managecampaigntemplates/basicsetup/modals/remove-campaign-target-sources-modal.c9f36421.html";
                this.modal.open({
                    animation: true,
                    templateUrl: modalTemplateUrl,
                    controller: "RemoveCampaignTargetSourcesModalController",
                    size: "lg",
                    backdrop: "static",
                    resolve: {
                        proceed: () => async () => {
                            this.campaignTemplate.CampaignTemplateTargetSources.splice(
                                0,
                                this.campaignTemplate.CampaignTemplateTargetSources.length
                            )
                            await this.loadTargetSources();
                        },
                        cancel: () => async () => {
                            await this.loadTargetSources();
                        },
                        campaignLevelId: () => this.campaignTemplate.CampaignLevelId,
                    }
                });
                return;
            }
            await this.loadTargetSources();
        }

        /**
         * @param {CampaignTargetSource} o
         */
        displayTextForTargetSource(o) {
            return o.Name;
        }
    }

    angular
        .module('dashboard')
        .component('manageCampaignTemplateBasic',
            {
                templateUrl: 'components/campaigndesigner/managecampaigntemplates/basicsetup/campaigntemplatebasicsetup.6b68d876.html',
                bindings: { campaignTemplate: '=?' },
                controller: [
                    '$scope',
                    '$state',
                    'CampaignDesignerService',
                    'PermissionService',
                    'notify',
                    'ErrorService',
                    'NgTableParams',
                    'LocalisationService',
                    '$uibModal',
                    ManageCampaignTemplateBasicController ]
            });
})();
